/**
 * ToneGenerator — Web Audio API synthesized telephone signaling tones.
 * Zero external audio files, zero latency, works across all browsers.
 *
 * Supported tones:
 * - Outgoing Ringback: Standard 440 Hz + 480 Hz dual-frequency PBX ringback tone
 * - Incoming Ringtone: Modern pleasant multi-tone phone chime
 * - DTMF keypad feedback tones (0-9, *, #)
 */

const DTMF_FREQS = {
  '1': [697, 1209],
  '2': [697, 1336],
  '3': [697, 1477],
  '4': [770, 1209],
  '5': [770, 1336],
  '6': [770, 1477],
  '7': [852, 1209],
  '8': [852, 1336],
  '9': [852, 1477],
  '*': [941, 1209],
  '0': [941, 1336],
  '#': [941, 1477],
};

class ToneGenerator {
  constructor() {
    this.audioCtx = null;
    this.ringbackTimer = null;
    this.ringtoneTimer = null;
    this.activeNodes = [];
    this.isRingbackPlaying = false;
    this.isRingtonePlaying = false;
  }

  getAudioContext() {
    if (typeof window === 'undefined') return null;
    if (!this.audioCtx || this.audioCtx.state === 'closed') {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        this.audioCtx = new AudioCtx();
      }
    }
    if (this.audioCtx && this.audioCtx.state === 'suspended') {
      this.audioCtx.resume().catch(() => {});
    }
    return this.audioCtx;
  }

  unlock() {
    const ctx = this.getAudioContext();
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
  }

  /**
   * Play a dual-frequency tone with smooth attack and release
   */
  _playDualTone(f1, f2, durationSec, gainLevel = 0.04) {
    const ctx = this.getAudioContext();
    if (!ctx) return null;

    try {
      const now = ctx.currentTime;
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gainNode = ctx.createGain();

      osc1.type = 'sine';
      osc2.type = 'sine';
      osc1.frequency.setValueAtTime(f1, now);
      osc2.frequency.setValueAtTime(f2, now);

      // Smooth attack and release to eliminate clicks
      const attack = 0.04;
      const release = 0.04;
      gainNode.gain.setValueAtTime(0.0001, now);
      gainNode.gain.linearRampToValueAtTime(gainLevel, now + attack);
      gainNode.gain.setValueAtTime(gainLevel, Math.max(now + attack, now + durationSec - release));
      gainNode.gain.linearRampToValueAtTime(0.0001, now + durationSec);

      osc1.connect(gainNode);
      osc2.connect(gainNode);
      gainNode.connect(ctx.destination);

      osc1.start(now);
      osc2.start(now);
      osc1.stop(now + durationSec);
      osc2.stop(now + durationSec);

      const nodeRef = { osc1, osc2, gainNode };
      this.activeNodes.push(nodeRef);

      const cleanup = () => {
        try {
          osc1.disconnect();
          osc2.disconnect();
          gainNode.disconnect();
        } catch (_) {}
        const idx = this.activeNodes.indexOf(nodeRef);
        if (idx !== -1) this.activeNodes.splice(idx, 1);
      };

      setTimeout(cleanup, (durationSec + 0.1) * 1000);
      return nodeRef;
    } catch (e) {
      console.warn('[ToneGenerator] error playing dual tone:', e);
      return null;
    }
  }

  /**
   * Start Outbound Ringback Tone: 440 Hz + 480 Hz (Standard PBX Ringback)
   * Cadence: 1.8s tone, 3.2s silence, repeated continuously until stopped
   */
  startRingback() {
    if (this.isRingbackPlaying) return;
    this.stopAll();
    this.isRingbackPlaying = true;

    const burstDuration = 1.8;
    const intervalMs = 5000;

    const playCycle = () => {
      if (!this.isRingbackPlaying) return;
      this._playDualTone(440, 480, burstDuration, 0.035);
      this.ringbackTimer = setTimeout(playCycle, intervalMs);
    };

    playCycle();
  }

  stopRingback() {
    this.isRingbackPlaying = false;
    if (this.ringbackTimer) {
      clearTimeout(this.ringbackTimer);
      this.ringbackTimer = null;
    }
    this._stopActiveNodes();
  }

  /**
   * Start Inbound Ringtone: Distinctive phone chime (C5 + E5 -> D5 + G5)
   * Cadence: 3 rings, 2.5s pause, repeated until answered or declined
   */
  startRingtone() {
    if (this.isRingtonePlaying) return;
    this.stopAll();
    this.isRingtonePlaying = true;

    const playCycle = () => {
      if (!this.isRingtonePlaying) return;
      // Chime 1
      this._playDualTone(523.25, 659.25, 0.35, 0.07);
      // Chime 2
      setTimeout(() => {
        if (!this.isRingtonePlaying) return;
        this._playDualTone(587.33, 783.99, 0.35, 0.07);
      }, 450);
      // Chime 3
      setTimeout(() => {
        if (!this.isRingtonePlaying) return;
        this._playDualTone(659.25, 1046.5, 0.45, 0.08);
      }, 900);

      this.ringtoneTimer = setTimeout(playCycle, 4000);
    };

    playCycle();
  }

  stopRingtone() {
    this.isRingtonePlaying = false;
    if (this.ringtoneTimer) {
      clearTimeout(this.ringtoneTimer);
      this.ringtoneTimer = null;
    }
    this._stopActiveNodes();
  }

  /**
   * Play instant DTMF tone when keypad button is pressed
   */
  playDtmf(digit) {
    const freqs = DTMF_FREQS[String(digit)];
    if (!freqs) return;
    this._playDualTone(freqs[0], freqs[1], 0.12, 0.04);
  }

  _stopActiveNodes() {
    const ctx = this.audioCtx;
    const now = ctx ? ctx.currentTime : 0;
    while (this.activeNodes.length > 0) {
      const { osc1, osc2, gainNode } = this.activeNodes.pop();
      try {
        if (gainNode && ctx) {
          gainNode.gain.cancelScheduledValues(now);
          gainNode.gain.setValueAtTime(0.0001, now);
        }
        if (osc1) { osc1.stop(); osc1.disconnect(); }
        if (osc2) { osc2.stop(); osc2.disconnect(); }
        if (gainNode) { gainNode.disconnect(); }
      } catch (_) {}
    }
  }

  stopAll() {
    this.stopRingback();
    this.stopRingtone();
    this._stopActiveNodes();
  }
}

export const toneGenerator = new ToneGenerator();
export default toneGenerator;
